// Configurazione navigazione condivisa: tipo View + items per sidebar/palette/mobile.
// I label sono chiavi i18n (namespace "shell"); i consumer (DesktopSidebar,
// NavSection, CommandPalette) le risolvono con useTranslation.
import {
  BarChart3,
  CalendarDays,
  CalendarRange,
  Goal,
  Shield,
  Shirt,
  Sparkles,
  UserPlus,
} from "lucide-react";
import i18n from "../../lib/i18n";
import { FcIcon } from "../brand";

export type View =
  | "home"
  | "players"
  | "compare-players"
  | "top11"
  | "archetypes"
  | "guests"
  | "sessions"
  | "stats"
  | "seasons"
  | "settings"
  | "player"
  | "admin"
  | "requests"
  | "ea"
  | "club";

// labelKey e' la chiave i18n nel namespace "shell.nav.<key>". Mantengo
// anche `label` come fallback (ricavato a runtime), perche' alcuni
// consumer storici si aspettano una string.
export const navItems: Array<{
  id: View;
  labelKey: string;
  icon: typeof BarChart3;
  section: "main" | "club" | "settings" | "admin";
}> = [
  { id: "home", labelKey: "nav.home", icon: Goal, section: "main" },
  { id: "sessions", labelKey: "nav.sessions", icon: CalendarDays, section: "main" },
  { id: "players", labelKey: "nav.players", icon: Shirt, section: "main" },
  { id: "stats", labelKey: "nav.stats", icon: BarChart3, section: "main" },
  { id: "ea", labelKey: "nav.ea", icon: FcIcon as typeof BarChart3, section: "club" },
  { id: "seasons", labelKey: "nav.seasons", icon: CalendarRange, section: "club" },
  { id: "guests", labelKey: "nav.guests", icon: UserPlus, section: "club" },
  { id: "requests", labelKey: "nav.requests", icon: Sparkles, section: "club" },
  { id: "settings", labelKey: "nav.settings", icon: Shield, section: "settings" },
  { id: "admin", labelKey: "nav.admin", icon: Shield, section: "admin" },
];

// Tab bar di telefono e tablet.
export const tabItems: Array<{ id: View; labelKey: string; icon: typeof BarChart3 }> = [
  ...navItems.filter((it) => it.section === "main"),
  { id: "club", labelKey: "nav.club", icon: Shield },
];

const ROSTER_VIEWS: View[] = ["players", "player", "compare-players", "top11", "archetypes"];
const CLUB_VIEWS: View[] = ["club", "ea", "seasons", "guests", "requests", "settings", "admin"];

export function tabForView(view: View): View {
  if (ROSTER_VIEWS.includes(view)) return "players";
  if (CLUB_VIEWS.includes(view)) return "club";
  return view;
}

// Sotto-voci dell'accordion "Rosa" nella sidebar: portano a view distinte
// (non a sezioni dentro la stessa view).
export type RosterSection = {
  id: string;
  labelKey: string;
  view: View;
};

export const rosterSections: RosterSection[] = [
  { id: "all", labelKey: "nav.rosterSections.all", view: "players" },
  { id: "compare", labelKey: "nav.rosterSections.compare", view: "compare-players" },
  { id: "top11", labelKey: "nav.rosterSections.top11", view: "top11" },
  { id: "archetypes", labelKey: "nav.rosterSections.archetypes", view: "archetypes" },
];

// Sotto-sezioni della pagina Impostazioni. `hash` e' l'anchor (id della
// <section> in SettingsView) usato per lo scroll diretto via URL hash.
// `adminOnly` limita la voce a founder/coach/app-admin.
export type SettingsSection = {
  id: string;
  labelKey: string;
  hash: string;
  adminOnly?: boolean;
};

export const settingsSections: SettingsSection[] = [
  { id: "team", labelKey: "nav.settingsSections.team", hash: "team" },
  { id: "squad-score", labelKey: "nav.settingsSections.squadScore", hash: "squad-score" },
  { id: "competitions", labelKey: "nav.settingsSections.competitions", hash: "competitions" },
  { id: "notifications", labelKey: "nav.settingsSections.notifications", hash: "notifications" },
  { id: "account", labelKey: "nav.settingsSections.account", hash: "account" },
  { id: "support", labelKey: "nav.settingsSections.support", hash: "support" },
  { id: "members", labelKey: "nav.settingsSections.members", hash: "members", adminOnly: true },
];

// Helper "puro" che legge dal default i18n (consumer non React).
export function navLabel(key: string): string {
  return i18n.t(key, { ns: "shell" }) as string;
}

export function viewTitle(view: View, teamName?: string): string {
  if (view === "ea" && teamName) {
    return i18n.t("title.eaWithTeam", { ns: "shell", teamName }) as string;
  }
  return i18n.t(`title.${view}`, { ns: "shell" }) as string;
}

export function viewBreadcrumb(view: View, teamName: string): string {
  return i18n.t(`breadcrumb.${view}`, { ns: "shell", teamName }) as string;
}
